import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLdapLogin } from '@/hooks/admin/Auth/useLdapLogin';
import { useLogin } from '@/hooks/admin/Auth/useLogin';
import { PublicAuthProviderApi } from '@/services/auth-provider.service';
import { AuthApi } from '@/services/auth.service';
import axiosInstance from '@/api/axiosInstance';
import { API_PREFIX } from '@/api/endpoints/api.constants';
import { useT, tr } from '@/i18n';
import { useAuth } from '@/contexts/shared/AuthContext';
import { TENANT_ENDPOINTS } from '@/api/endpoints/tenant.endpoints';
import { tokenStorage } from '@/api/tokenManager';
import { startSso } from '@/auth/ssoHandoff';
import { localizeAuthMessage } from '@/auth/authMessages';
import { CHANGE_PASSWORD_PATH } from '@/auth/passwordChange';
import type { TokenResponse } from '@/types/authentication.types';

// Built on every render, not at module load, so the messages are in the
// interface language (useForm reads the resolver on each render).
const makeLoginSchema = () => z.object({
    email: z.string().min(1, tr("login.validation.email_required", "Email is required")),
    password: z.string().min(1, tr("login.validation.password_required", "Password is required")),
});

const makeSignupSchema = () => z.object({
    first_name: z.string().min(1, tr("ui.first_name_is_required", "First name is required")),
    last_name: z.string().min(1, tr("ui.last_name_is_required", "Last name is required")),
    email: z.string().email(tr("login.validation.email_invalid", "Invalid email address")),
    password: z.string().min(8, tr("ui.password_must_be_at_least_8_characters", "Password must be at least 8 characters")),
    confirm_password: z.string().min(1, tr("ui.please_confirm_your_password", "Please confirm your password")),
}).refine(data => data.password === data.confirm_password, {
    message: tr("ui.passwords_do_not_match", "Passwords do not match."),
    path: ['confirm_password'],
});

const makeForgotSchema = () => z.object({
    email: z.string().email(tr("login.validation.email_invalid", "Invalid email address")),
});

const makeNewPasswordSchema = () => z.object({
    password: z.string().min(8, tr("ui.password_must_be_at_least_8_characters", "Password must be at least 8 characters")),
    confirm_password: z.string().min(1, tr("ui.please_confirm_your_password", "Please confirm your password")),
}).refine(data => data.password === data.confirm_password, {
    message: tr("ui.passwords_do_not_match", "Passwords do not match."),
    path: ['confirm_password'],
});

const makeLdapSchema = () => z.object({
    username: z.string().min(1, tr("ui.username_is_required", "Username is required")),
    password: z.string().min(1, tr("login.validation.password_required", "Password is required")),
});

type LoginFormValues = z.infer<ReturnType<typeof makeLoginSchema>>;
type SignupFormValues = z.infer<ReturnType<typeof makeSignupSchema>>;
type LdapFormValues = z.infer<ReturnType<typeof makeLdapSchema>>;

export const useLoginPage = () => {
    useT();
    const { handleLogin } = useLogin();
    const { handleLdapLogin } = useLdapLogin();
    const { checkAuth } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // The URL the user originally tried to visit before being redirected to login.
    // PrivateRoute passes it via router state as `{ from: location }`.
    const intendedLocation = (location.state as any)?.from;

    // `/login?signup=1` (e.g. the homepage "Sign up" buttons) opens the registration view.
    const [view, setView] = useState<'login' | 'signup' | 'forgot' | 'reset-password' | 'ldap' | 'forgot-success' | 'reset-success'>(
        () => (new URLSearchParams(window.location.search).get('signup') ? 'signup' : 'login'),
    );
    const [showPassword, setShowPassword] = useState(false);
    // The API sends a failed SSO sign-in back to /login?error=oauth_failed|saml_failed|sso_failed;
    // the idle watcher (auth/idleSession.ts) signs out to /login?expired=1.
    const [globalError, setGlobalError] = useState(() => {
        const params = new URLSearchParams(location.search);
        if (/^(oauth|saml|sso)_failed$/.test(params.get('error') || '')) {
            return tr("login.sso_failed", "Single sign-on failed. Please try again or contact your administrator.");
        }
        if (params.get('expired')) {
            return tr("login.session_expired", "You were signed out after a period of inactivity. Please sign in again.");
        }
        return '';
    });

    // The e-mail confirmation link of a self-registration comes back here with
    // the outcome: /login?registration=confirmed|pending|invalid.
    const [registrationNotice, setRegistrationNotice] = useState<'confirmed' | 'pending' | 'invalid' | null>(() => {
        const r = new URLSearchParams(location.search).get('registration');
        return r === 'confirmed' || r === 'pending' || r === 'invalid' ? r : null;
    });

    const [mfaChallenge, setMfaChallenge] = useState<{
        mfaToken: string;
        allowed: string[];
        enrolled: string[];
        mustEnrol: boolean;
        emailHint?: string;
    } | null>(null);

    const [selectedLdapProvider, setSelectedLdapProvider] = useState<any | null>(null);
    const [ssoItems, setSsoItems] = useState<{ label: string; onClick: () => void }[]>([]);
    const ssoTriggerRef = useRef<HTMLButtonElement>(null);
    const [isSsoOpen, setIsSsoOpen] = useState(false);

    const loginForm = useForm<LoginFormValues>({ resolver: zodResolver(makeLoginSchema()) });
    const signupForm = useForm<SignupFormValues>({ resolver: zodResolver(makeSignupSchema()) });
    const forgotForm = useForm({ resolver: zodResolver(makeForgotSchema()) });
    const newPasswordForm = useForm({ resolver: zodResolver(makeNewPasswordSchema()) });
    const ldapForm = useForm<LdapFormValues>({ resolver: zodResolver(makeLdapSchema()) });

    useEffect(() => {
        const hash = location.hash.replace('#', '');
        if (['reset-password', 'forgot', 'signup', 'ldap', 'forgot-success', 'reset-success'].includes(hash)) {
            setView(hash as any);
            setGlobalError(''); // clear error only when switching to another view
        } else {
            // No hash: the landing page's "Sign up" links arrive as /login?signup=1 — keep
            // that view instead of resetting to login (UAT DEF-AU-007).
            setView(new URLSearchParams(location.search).get('signup') ? 'signup' : 'login');
            // Don't clear error here — user may have just submitted wrong credentials
        }
    }, [location.hash, location.search]);

    useEffect(() => {
        const fetchProviders = async () => {
            try {
                const providers = await PublicAuthProviderApi.getPublicProviders();
                const items = providers.map((provider) => ({
                    label: provider.name,
                    onClick: () => {
                        if (provider.protocol === 'ldap') {
                            setSelectedLdapProvider(provider);
                            setIsSsoOpen(false);
                            navigate('#ldap');
                        } else {
                            // Persist the intended destination before the full-page OAuth redirect.
                            // OAuthCallbackPage will read it back after authentication completes.
                            if (intendedLocation) {
                                const path = intendedLocation.pathname + (intendedLocation.search || '') + (intendedLocation.hash || '');
                                sessionStorage.setItem('oauth_redirect_after_login', path);
                            }
                            startSso(provider.protocol === 'saml' ? 'saml' : 'oauth', provider.id).catch(() =>
                                setGlobalError(tr("login.sso_failed", "Single sign-on failed. Please try again or contact your administrator.")));
                        }
                    }
                }));
                setSsoItems(items);
            } catch (error) {
                setGlobalError(tr("ui.failed_to_load_authentication_providers", "Failed to load authentication providers."));
            }
        };
        fetchProviders();
    },[]);



    /**
     * Resolve where to send the user after a successful login.
     * Priority:
     *   1. The route they originally tried to visit (router state `from`).
     *   2. Fall back to /user/dashboard.
     */
    const getPostLoginPath = () => {
        if (intendedLocation) {
            const path = intendedLocation.pathname + (intendedLocation.search || '') + (intendedLocation.hash || '');
            return path || '/user/dashboard';
        }
        return '/user/dashboard';
    };

    /** `mustChangePassword`: the sign-in answer says an administrator asked for a new password —
     *  go to "Choose a new password" first; it continues to the intended page afterwards. */
    const fetchTenantsAndShowView = async (mustChangePassword = false) => {
        const destination = mustChangePassword ? CHANGE_PASSWORD_PATH : getPostLoginPath();
        const state = mustChangePassword && intendedLocation ? { from: intendedLocation } : undefined;
        try {
            const response = await axiosInstance.get(TENANT_ENDPOINTS.MY_TENANTS);
            const tenants = response.data;
            if (tenants && tenants.length > 0) {
                const defaultTenant = tenants.find((t: any) => t.is_default) || tenants[0];
                await switchTenantContext(defaultTenant.id, destination, state);
            } else {
                await checkAuth();
                navigate(destination, { replace: true, state });
            }
        } catch (error) {
            await checkAuth();
            navigate(destination, { replace: true, state });
        }
    };

    const switchTenantContext = async (tenantId: string | number, destination = '/user/dashboard', state?: object) => {
        try {
            const response = await axiosInstance.post(TENANT_ENDPOINTS.SWITCH_TENANT(tenantId));
            
            if (response.data && response.data.access_token) {
                const currentRefresh = tokenStorage.getRefreshToken() || "";
                const newRefresh = (response.data.refresh_token && response.data.refresh_token !== "") 
                    ? response.data.refresh_token 
                    : currentRefresh;
                
                tokenStorage.setTokens(response.data.access_token, newRefresh);
                localStorage.setItem('tenant_id', tenantId.toString());
            }
            
            await checkAuth();
            navigate(destination, { replace: true, state });
        } catch (error) {
            await checkAuth();
            navigate(destination, { replace: true, state });
        }
    };

    const onLoginSubmit = async (data: LoginFormValues) => {
        setGlobalError('');
        setRegistrationNotice(null);
        try {
            const res: any = await handleLogin({ email: data.email.trim().toLowerCase(), password: data.password });
            // MFA gate: the backend returns a pending challenge instead of tokens.
            if (res?.mfa_required) {
                setMfaChallenge({
                    mfaToken: res.mfa_token,
                    allowed: res.allowed_methods || [],
                    enrolled: res.enrolled_methods || [],
                    mustEnrol: !!res.must_enrol,
                    emailHint: res.email_hint,
                });
                return;
            }
            await fetchTenantsAndShowView(!!res?.force_password_change);
        } catch (error: any) {
            const detail = error.response?.data?.detail;
            if (error.response && error.response.status === 401) {
                setGlobalError(tr("ui.invalid_username_or_password", "Invalid username or password."));
            } else if (error.response && typeof detail === 'string' && detail) {
                // A refusal with a reason (account locked, suspended, tenant
                // inactive…) — show it; it was wrongly reported as a network
                // error before (UAT DEF-AU-001).
                setGlobalError(localizeAuthMessage(detail));
            } else if (error.request && !error.response) {
                setGlobalError(tr("login.error_network", "Network error. Server is unavailable."));
            } else {
                setGlobalError(tr("ui.an_error_occurred_x", "An error occurred: {v0}", { v0: error.message }));
            }
        }
    };

    const onSignupSubmit = async (data: SignupFormValues) => {
        setGlobalError('');
        try {
            await AuthApi.register({
                username: data.email,
                firstname: data.first_name,
                lastname: data.last_name,
                email: data.email,
                lang: 'en',
                mobile: '',
                summary: '',
                loginmethod: 'manual',
                sync: true,
                locked: false,
                password: data.password,
                role_ids: []
            });
            signupForm.reset();
            navigate('#');
        } catch (error: any) {
            setGlobalError(localizeAuthMessage(error.response?.data?.message || error.response?.data?.detail) || tr("ui.registration_failed", "Registration failed."));
        }
    };

    // UAT DEF-173: self-service reset. The request always gets the same answer (no account enumeration);
    // the e-mailed link opens /login?reset_token=…#reset-password.
    const onForgotSubmit = async (data: { email: string }) => {
        setGlobalError('');
        try {
            await axiosInstance.post(`${API_PREFIX}/auth/password-reset/request`, { email: data.email.trim() });
        } catch {
            // Same screen either way: it never says whether the address has an account.
        }
        navigate('#forgot-success');
    };

    const onNewPasswordSubmit = async (data: { password: string }) => {
        setGlobalError('');
        const token = new URLSearchParams(window.location.search).get('reset_token');
        if (!token) {
            setGlobalError(tr("login.reset_link_invalid", "This reset link is invalid or has expired. Ask for a new one."));
            return;
        }
        try {
            await axiosInstance.post(`${API_PREFIX}/auth/password-reset/confirm`, { token, new_password: data.password });
            navigate('/login#reset-success', { replace: true });
        } catch (error: any) {
            // Password-policy refusals come back as a sentence; anything else (expired link, validation) gets the generic text.
            const detail = error.response?.data?.detail;
            setGlobalError(localizeAuthMessage(detail) || tr("login.reset_link_invalid", "This reset link is invalid or has expired. Ask for a new one."));
        }
    };

    const onLdapSubmit = async (data: LdapFormValues) => {
        setGlobalError('');
        if (selectedLdapProvider) {
            try {
                await handleLdapLogin(selectedLdapProvider.id, data.username, data.password);
                await fetchTenantsAndShowView();
            } catch (error: any) {
                if (error.response && error.response.status === 401) {
                    setGlobalError(tr("ui.invalid_ldap_username_or_password", "Invalid LDAP username or password."));
                } else {
                    setGlobalError(tr("ui.ldap_login_failed", "LDAP login failed."));
                }
            }
        }
    };

    const handleBackToLogin = () => {
        setSelectedLdapProvider(null);
        setGlobalError('');
        loginForm.reset();
        signupForm.reset();
        forgotForm.reset();
        newPasswordForm.reset();
        ldapForm.reset();
        // Arrived as /login?signup=1 (homepage "Sign up"): drop the query too, or the page stays on sign-up
        // ("Log in" / "Already have an account?" did nothing). Keep where to go after signing in.
        if (new URLSearchParams(location.search).get('signup')) {
            navigate({ pathname: location.pathname, search: '', hash: '' }, { state: location.state });
        } else {
            navigate('#');
        }
    };

    // MFA challenge resolved (tokens already stored by the challenge component).
    const completeMfa = async (res?: TokenResponse) => {
        setMfaChallenge(null);
        await fetchTenantsAndShowView(!!res?.force_password_change);
    };
    const cancelMfa = () => {
        setMfaChallenge(null);
        setGlobalError('');
        loginForm.reset();
    };

    const togglePasswordVisibility = () => setShowPassword(!showPassword);
    const toggleSsoMenu = () => setIsSsoOpen(!isSsoOpen);
    const closeSsoMenu = () => setIsSsoOpen(false);

    return {
        view,
        globalError, setGlobalError,
        showPassword, togglePasswordVisibility,
        forms: {
            login: loginForm,
            signup: signupForm,
            forgot: forgotForm,
            newPassword: newPasswordForm,
            ldap: ldapForm,
        },
        handlers: {
            onLoginSubmit: loginForm.handleSubmit(onLoginSubmit),
            onSignupSubmit: signupForm.handleSubmit(onSignupSubmit),
            onForgotSubmit: forgotForm.handleSubmit(onForgotSubmit),
            onNewPasswordSubmit: newPasswordForm.handleSubmit(onNewPasswordSubmit),
            onLdapSubmit: ldapForm.handleSubmit(onLdapSubmit),
            handleBackToLogin,
        },
        sso: {
            items: ssoItems,
            isOpen: isSsoOpen,
            toggleMenu: toggleSsoMenu,
            closeMenu: closeSsoMenu,
            triggerRef: ssoTriggerRef,
            selectedLdapProvider
        },
        registrationNotice,
        mfaChallenge,
        completeMfa,
        cancelMfa,
        navigate
    };
};